/**
 * Camada de veículos sobre o Google Maps do MapPage (F11).
 * Renderiza marcadores customizados (carro SVG colorido por status), InfoWindow
 * com metadata + speed + idade, e polyline opcional do trail (último Nh).
 *
 * Auto-refresh do /api/fleet/vehicles a cada 30s.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AdvancedMarker, InfoWindow, useMap } from '@vis.gl/react-google-maps';
import { api } from '../api';

type Vehicle = {
  id: string;
  traccarDeviceId: number;
  label: string;
  plate: string | null;
  model: string | null;
  group: string | null;
  color: string | null;
  lastFixAt: string | null;
  ageSec: number | null;
  lat: number | null;
  lng: number | null;
  speedKmh: number | null;
  course: number | null;
  status: 'online' | 'idle' | 'offline' | 'never';
};

type FleetResp = {
  summary: {
    total: number; online: number; idle: number; offline: number; never: number;
    byGroup: Record<string, number>;
  };
  vehicles: Vehicle[];
};

const STATUS_COLOR: Record<Vehicle['status'], string> = {
  online:  '#10b981',
  idle:    '#f59e0b',
  offline: '#64748b',
  never:   '#475569'
};

function fmtAge(sec: number | null): string {
  if (sec == null) return 'sem fix';
  if (sec < 60) return `${sec}s atrás`;
  if (sec < 3600) return `${Math.round(sec / 60)}min atrás`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h atrás`;
  return `${Math.round(sec / 86400)}d atrás`;
}

function CarIcon({ color, course }: { color: string; course: number | null }) {
  // SVG simples de carro visto de cima, rotacionado pelo course (0 = norte, 90 = leste).
  const rot = course != null ? course : 0;
  return (
    <div style={{ width: 22, height: 22, transform: `rotate(${rot}deg)` }} title={`${color}`}>
      <svg viewBox="0 0 24 24" width="22" height="22" style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }}>
        <path d="M12 1 L7 8 L7 22 L12 19 L17 22 L17 8 Z" fill={color} stroke="#0f172a" strokeWidth="1.5" strokeLinejoin="round" />
        <circle cx="12" cy="11" r="1.3" fill="#0f172a" />
      </svg>
    </div>
  );
}

export function FleetLayer({ enabled, filter }: { enabled: boolean; filter: 'all' | 'online' | 'idle' | 'offline' }) {
  const q = useQuery<FleetResp>({
    queryKey: ['fleet', 'vehicles'],
    queryFn: () => api.get('/api/fleet/vehicles'),
    refetchInterval: 30_000,
    refetchIntervalInBackground: true,
    enabled
  });
  const [selected, setSelected] = useState<Vehicle | null>(null);

  if (!enabled || !q.data) return null;
  const vehicles = q.data.vehicles
    .filter((v) => v.lat != null && v.lng != null)
    .filter((v) => filter === 'all' ? true : v.status === filter);

  return (
    <>
      {vehicles.map((v) => (
        <AdvancedMarker key={v.id} position={{ lat: v.lat!, lng: v.lng! }} onClick={() => setSelected(v)}>
          <CarIcon color={STATUS_COLOR[v.status]} course={v.course} />
        </AdvancedMarker>
      ))}
      {selected && selected.lat != null && selected.lng != null && (
        <InfoWindow position={{ lat: selected.lat, lng: selected.lng }} onCloseClick={() => setSelected(null)}>
          <VehicleInfo v={selected} />
        </InfoWindow>
      )}
    </>
  );
}

function VehicleInfo({ v }: { v: Vehicle }) {
  const [showTrail, setShowTrail] = useState(false);
  return (
    <div style={{ color: '#0f172a', minWidth: 240 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{v.label}</div>
      {v.plate && <div style={{ fontSize: 11 }}><code style={{ background: '#e2e8f0', padding: '1px 4px', borderRadius: 3 }}>{v.plate}</code></div>}
      {v.group && <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>{v.group}{v.model ? ` · ${v.model}` : ''}</div>}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, fontSize: 11 }}>
        <span style={{
          padding: '1px 6px', borderRadius: 3, color: '#fff',
          background: STATUS_COLOR[v.status]
        }}>{v.status}</span>
        <span style={{ color: '#475569' }}>{fmtAge(v.ageSec)}</span>
        {v.speedKmh != null && <span style={{ color: '#475569' }}>· {Math.round(v.speedKmh)} km/h</span>}
      </div>
      <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4, fontFamily: 'monospace' }}>
        {v.lat!.toFixed(5)}, {v.lng!.toFixed(5)}
      </div>
      <button onClick={() => setShowTrail((s) => !s)}
              style={{ marginTop: 8, fontSize: 11, color: '#0284c7', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
        {showTrail ? 'esconder trail' : 'mostrar trail (1h)'}
      </button>
      {showTrail && <VehicleTrail vehicleId={v.id} />}
    </div>
  );
}

function VehicleTrail({ vehicleId }: { vehicleId: string }) {
  const map = useMap();
  const q = useQuery<{ vehicle: any; range: any; points: Array<{ ts: string; lat: number; lng: number; speedKmh: number | null }> }>({
    queryKey: ['fleet', 'track', vehicleId, '1h'],
    queryFn: () => api.get(`/api/fleet/vehicles/${vehicleId}/track?hours=1`),
    staleTime: 60_000
  });
  useEffect(() => {
    if (!map || !window.google?.maps || !q.data) return;
    const path = q.data.points.map((p) => ({ lat: p.lat, lng: p.lng }));
    if (path.length < 2) return;
    const poly = new (window as any).google.maps.Polyline({
      path,
      strokeColor: '#0284c7',
      strokeOpacity: 0.85,
      strokeWeight: 3,
      icons: [{ icon: { path: (window as any).google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2 }, offset: '50%', repeat: '60px' }],
      map
    });
    return () => { poly.setMap(null); };
  }, [map, q.data]);
  if (q.isLoading) return <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>carregando trail…</div>;
  if (q.isError) return <div style={{ fontSize: 11, color: '#e11d48', marginTop: 4 }}>erro ao carregar trail</div>;
  return <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>{q.data?.points.length} pontos na última 1h</div>;
}

export function FleetToggle({ enabled, setEnabled, filter, setFilter, summary }: {
  enabled: boolean;
  setEnabled: (b: boolean) => void;
  filter: 'all' | 'online' | 'idle' | 'offline';
  setFilter: (f: 'all' | 'online' | 'idle' | 'offline') => void;
  summary?: FleetResp['summary'];
}) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <button onClick={() => setEnabled(!enabled)}
              className={`px-2 py-1 rounded ${enabled ? 'bg-cyan-600 text-white' : 'bg-slate-800 text-slate-300'} hover:bg-cyan-500`}>
        🚗 Carros{summary ? ` (${summary.total})` : ''}
      </button>
      {enabled && summary && (
        <select className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs"
                value={filter} onChange={(e) => setFilter(e.target.value as any)}>
          <option value="all">todos ({summary.total})</option>
          <option value="online">online ({summary.online})</option>
          <option value="idle">parados ({summary.idle})</option>
          <option value="offline">offline ({summary.offline})</option>
        </select>
      )}
    </div>
  );
}

export function useFleetSummary(enabled: boolean) {
  return useQuery<FleetResp>({
    queryKey: ['fleet', 'vehicles'],
    queryFn: () => api.get('/api/fleet/vehicles'),
    refetchInterval: 30_000,
    refetchIntervalInBackground: true,
    enabled
  });
}
